import React from 'react'
import { cityName, type RouteEndpoint } from '../../../components/dispatch/RouteDisplay'

/** Legal suffixes carry no information on a route line — "Inc" is not the stop. */
const LEGAL_SUFFIX = /[,\s]+(?:inc|incorporated|llc|l\.l\.c|corp|corporation|co|company|ltd|limited|lp|llp)\.?$/i

/** CJK glyphs take about two Latin characters' width, so budget them as two. */
const WIDE_GLYPH = /[⺀-鿿가-힯＀-｠]/

const displayWidth = (text: string): number => {
  let width = 0
  for (const ch of text) width += WIDE_GLYPH.test(ch) ? 2 : 1
  return width
}

/**
 * The stop label as it fits on a driver card.
 *
 * A warehouse with no short name configured falls back to its legal name —
 * "Rene Kress Stacked Energy, Inc" — which is wider than the cell it lives in,
 * so it ran over the next stop and the two names drew on top of each other.
 *
 * Trimming is by meaning first: drop the legal suffix, then cut on a word
 * boundary, so what survives is the part a dispatcher recognises. The full name
 * is always on the cell's tooltip, so nothing is actually lost.
 */
export function compactStopName(name: string, maxWidth = 20): string {
  const trimmed = name.trim().replace(LEGAL_SUFFIX, '').trim()
  if (!trimmed) return name.trim()
  if (displayWidth(trimmed) <= maxWidth) return trimmed

  // Prefer a whole-word cut; a name chopped mid-word reads as a typo.
  let kept = ''
  for (const word of trimmed.split(/\s+/)) {
    const next = kept ? `${kept} ${word}` : word
    if (displayWidth(next) > maxWidth) break
    kept = next
  }
  if (kept) return `${kept}…`

  // One long unbroken token: cut it by width instead.
  let cut = ''
  for (const ch of trimmed) {
    if (displayWidth(cut + ch) > maxWidth - 1) break
    cut += ch
  }
  return `${cut}…`
}

/**
 * One end of a route: node short name and city on one line, with the
 * appointment that belongs to *that* node underneath.
 *
 * The appointment travels with the endpoint rather than being paired by
 * position, because which one applies depends on the node's type and the task
 * direction — an empty-in starts at the warehouse, so a fixed "port on the
 * left, warehouse on the right" pairing is wrong half the time.
 *
 * Colour carries the appointment's state: green is booked and settled, red is
 * booked on a date that has already gone by, amber is still owed, grey NAR is
 * the one case with nothing to chase. Labels and colours match the Route column
 * so a stop never reads differently between views.
 *
 * Shared by the driver task cards and the confirmation drawer — the two places
 * a dispatcher compares routes side by side, so they must not drift apart.
 *
 * `quiet` 收掉的只有那个绿：约好了、日子也没过 —— 是每张卡上都有的常态，而常态
 * 不需要颜色（「不是红的」已经把这件事说完了）。派送确认窗口只留三个颜色，绿在
 * 那儿会跟真正在报警的红/橙抢眼睛。红、橙、Rebook 一个不动：它们说的是有事。
 */
export const RouteEndpointCell: React.FC<{ endpoint: RouteEndpoint; quiet?: boolean }> = ({ endpoint, quiet }) => {
  const city = cityName(endpoint.city)
  return (
    <div
      // Both ends left-align so each stop's appointment sits under its own name.
      // Justifying them to opposite edges tears the pair apart across the panel
      // and stops reading as one route.
      className="flex flex-col min-w-0 flex-1 items-start text-left"
      title={endpoint.fullName || endpoint.shortName}
    >
      {/* Name and city share a line. The city gives up width first — the name is
          the part worth protecting — but the name truncates too rather than
          refusing to shrink: a name that can't shrink overflows its cell and
          draws on top of the next stop, which is worse than an ellipsis. */}
      <span className="flex items-baseline gap-1 min-w-0 max-w-full">
        <span className={`text-sm font-semibold truncate min-w-0 ${
          endpoint.rejected ? 'text-slate-400 line-through' : 'text-slate-900'
        }`}>
          {endpoint.shortName ? compactStopName(endpoint.shortName) : 'TBD'}
        </span>
        {city && (
          <span className="text-xs text-slate-600 truncate min-w-0 shrink-[3]">· {city}</span>
        )}
      </span>
      {endpoint.appt === 'nar' ? (
        <span className="text-xs text-slate-400 leading-tight">NAR</span>
      ) : endpoint.appt === 'rebook' ? (
        // 票废了（封闭区解除时已经来不及）—— 和 Route 列同一个词、同一个颜色。
        <span
          className="text-xs font-semibold text-red-600 leading-tight"
          title="Closed area lifted too late for this slot — book a new appointment"
        >Rebook</span>
      ) : endpoint.appt === 'pending' ? (
        <span className="text-xs font-semibold text-amber-600 leading-tight">Pending</span>
      ) : endpoint.appt ? (
        <span
          className={`text-xs font-semibold leading-tight truncate max-w-full ${
            endpoint.apptPast ? 'text-red-600' : quiet ? 'text-slate-700' : 'text-emerald-600'
          }`}
          title={endpoint.apptPast ? 'Appointment date has passed' : undefined}
        >
          {endpoint.appt}
        </span>
      ) : (
        <span className="text-xs text-slate-300 leading-tight">—</span>
      )}
    </div>
  )
}
